import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, RotateCcw, Package, Percent, Plus, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY_NEW = { title: "", price: "", compare_at_price: "", sku: "", available: true };

export default function VariantsSheet({ product, open, onOpenChange, onSaved }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [discountPct, setDiscountPct] = useState("");
  const [keepAsCompare, setKeepAsCompare] = useState(true);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [newV, setNewV] = useState(EMPTY_NEW);
  const [creating, setCreating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (product && open) {
      setRows((product.variants || []).map((v) => ({ ...v, dirty: false, original: { ...v } })));
      setDiscountPct("");
      setNewV(EMPTY_NEW);
      setAddOpen(false);
    }
  }, [product, open]);

  const patch = (variantId, patchObj) => {
    setRows((cur) => cur.map((v) => (v.id === variantId ? { ...v, ...patchObj, dirty: true } : v)));
  };
  const reset = (variantId) => {
    setRows((cur) => cur.map((v) => (v.id === variantId ? { ...v.original, dirty: false, original: v.original } : v)));
  };

  const save = async (v) => {
    if (!product?.id || !v?.id) return;
    setSaving((s) => ({ ...s, [v.id]: true }));
    try {
      // Send title VERBATIM as typed (no trim, no "Default" fallback). Preserves the
      // exact string the admin sees so PDP shows identical text.
      const r = await api.patch(`/admin/products/${product.id}/variants/${v.id}`, {
        title: v.title || "",
        price: Number(v.price) || 0,
        compare_at_price: v.compare_at_price ? Number(v.compare_at_price) : null,
        sku: v.sku || "",
        available: !!v.available,
      });
      toast.success(`Variante "${(v.title || "").slice(0, 40)}" aggiornata`);
      // Rebuild ALL rows from the fresh backend response — this prevents drift between
      // local edited state and DB, and keeps the sheet usable after multiple saves.
      const fresh = r.data?.variants || [];
      setRows(fresh.map((x) => ({ ...x, dirty: false, original: { ...x } })));
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving((s) => ({ ...s, [v.id]: false }));
    }
  };

  const removeVariant = async (v) => {
    if (!product?.id || !v?.id) return;
    if (rows.length <= 1) {
      toast.error("Non puoi eliminare l'ultima variante");
      return;
    }
    if (!window.confirm(`Eliminare la variante "${(v.title || "").slice(0, 60)}"?`)) return;
    setDeleting((s) => ({ ...s, [v.id]: true }));
    try {
      const r = await api.delete(`/admin/products/${product.id}/variants/${v.id}`);
      toast.success("Variante eliminata");
      setRows((r.data.variants || []).map((x) => ({ ...x, dirty: false, original: { ...x } })));
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    } finally {
      setDeleting((s) => ({ ...s, [v.id]: false }));
    }
  };

  const createVariant = async () => {
    const title = (newV.title || "").trim();
    const price = Number(newV.price);
    if (!title) { toast.error("Serve un titolo per la variante"); return; }
    if (!Number.isFinite(price) || price <= 0) { toast.error("Prezzo non valido"); return; }
    setCreating(true);
    try {
      const r = await api.post(`/admin/products/${product.id}/variants`, {
        title,
        price,
        compare_at_price: newV.compare_at_price ? Number(newV.compare_at_price) : null,
        sku: newV.sku || "",
        available: !!newV.available,
      });
      toast.success(`Variante "${title}" creata`);
      setRows((r.data.variants || []).map((v) => ({ ...v, dirty: false, original: { ...v } })));
      setNewV(EMPTY_NEW);
      setAddOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione variante");
    } finally {
      setCreating(false);
    }
  };

  const dirtyCount = useMemo(() => rows.filter((r) => r.dirty).length, [rows]);
  const minPrice = useMemo(() => {
    const prices = rows.map((r) => Number(r.price) || 0).filter((p) => p > 0);
    return prices.length ? Math.min(...prices) : 0;
  }, [rows]);

  const copyVariantLink = async (v) => {
    if (!product?.id || !v?.id) return;
    // Prefer the SEO handle when present (matches how the customer sees the URL);
    // fall back to the internal id, both are accepted by the /products/:id endpoint.
    const slug = product.shopify_handle || product.id;
    const origin = typeof window !== "undefined" ? window.location.origin : "https://audrivio.com";
    const url = `${origin}/prodotto/${slug}?v=${v.id}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers (e.g. HTTP contexts): temporary textarea + execCommand.
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Link variante copiato negli appunti");
    } catch (e) {
      toast.error("Impossibile copiare. Link: " + url);
    }
  };

  const applyBulkDiscount = async () => {
    const pct = Number(discountPct);
    if (!Number.isFinite(pct) || pct <= 0 || pct >= 100) {
      toast.error("Inserisci una percentuale tra 1 e 99");
      return;
    }
    if (!window.confirm(`Applicare -${pct}% a tutte le ${rows.length} varianti?`)) return;
    setApplyingDiscount(true);
    try {
      const r = await api.post(`/admin/products/${product.id}/variants/bulk-discount`, {
        percent: pct,
        keep_original_as_compare_at: keepAsCompare,
      });
      toast.success(`Sconto -${pct}% applicato a tutte le varianti`);
      setRows((r.data.variants || []).map((v) => ({ ...v, dirty: false, original: { ...v } })));
      setDiscountPct("");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore applicazione sconto");
    } finally {
      setApplyingDiscount(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-xd-border sticky top-0 bg-white z-10">
          <SheetTitle className="font-admin text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-xd-cyanHover" />
            Varianti prodotto
          </SheetTitle>
          <SheetDescription className="text-xs">
            {product?.name} · {rows.length} varianti · Prezzo da: € {minPrice.toFixed(2)}
            {dirtyCount > 0 && <span className="ml-2 text-xd-orange font-medium">{dirtyCount} modifiche non salvate</span>}
          </SheetDescription>
        </SheetHeader>

        <div className="p-4 space-y-3">
          {/* Add new variant panel */}
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 space-y-3" data-testid="add-variant-panel">
            {!addOpen ? (
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                data-testid="add-variant-open-btn"
                className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900 py-1"
              >
                <Plus className="w-4 h-4" /> Aggiungi nuova variante
              </button>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                  <Plus className="w-4 h-4" /> Nuova variante
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted">Titolo (es. 8+128GB — 10.1 pollici)</label>
                    <Input
                      value={newV.title}
                      onChange={(e) => setNewV({ ...newV, title: e.target.value })}
                      data-testid="new-variant-title"
                      className="mt-1"
                      placeholder="Descrivi la versione (RAM, storage, schermo…)"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted">Prezzo *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newV.price}
                      onChange={(e) => setNewV({ ...newV, price: e.target.value })}
                      data-testid="new-variant-price"
                      className="mt-1"
                      placeholder="349.00"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted">Prezzo pieno (barrato)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newV.compare_at_price}
                      onChange={(e) => setNewV({ ...newV, compare_at_price: e.target.value })}
                      data-testid="new-variant-cap"
                      className="mt-1"
                      placeholder="—"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted">SKU</label>
                    <Input
                      value={newV.sku}
                      onChange={(e) => setNewV({ ...newV, sku: e.target.value })}
                      data-testid="new-variant-sku"
                      className="mt-1"
                      placeholder="—"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer text-xd-text mt-6">
                    <Switch
                      checked={!!newV.available}
                      onCheckedChange={(val) => setNewV({ ...newV, available: val })}
                      data-testid="new-variant-available"
                    />
                    Disponibile subito
                  </label>
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => { setAddOpen(false); setNewV(EMPTY_NEW); }}
                    className="text-xs text-xd-muted hover:text-xd-text"
                    data-testid="new-variant-cancel"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={createVariant}
                    disabled={creating || !newV.title || !newV.price}
                    data-testid="new-variant-create-btn"
                    className="btn-cta rounded-lg px-4 py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  >
                    {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Crea variante
                  </button>
                </div>
              </>
            )}
          </div>

          {rows.length > 0 && (
            <div className="rounded-lg bg-xd-cyan/5 border border-xd-cyan/20 p-4 space-y-3" data-testid="bulk-discount-panel">
              <div className="flex items-center gap-2 text-sm font-medium text-xd-cyanHover">
                <Percent className="w-4 h-4" />
                Sconto in blocco su tutte le varianti
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="99"
                  value={discountPct}
                  onChange={(e) => setDiscountPct(e.target.value)}
                  data-testid="bulk-discount-input"
                  placeholder="es. 15"
                  className="w-24"
                />
                <span className="text-sm text-xd-muted">%</span>
                <label className="flex items-center gap-2 text-xs cursor-pointer text-xd-text">
                  <Switch
                    checked={keepAsCompare}
                    onCheckedChange={setKeepAsCompare}
                    data-testid="bulk-discount-keep-toggle"
                  />
                  Mostra prezzo pieno barrato
                </label>
                <button
                  type="button"
                  onClick={applyBulkDiscount}
                  disabled={applyingDiscount || !discountPct}
                  data-testid="bulk-discount-apply-btn"
                  className="btn-cta rounded-lg px-4 py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 ml-auto"
                >
                  {applyingDiscount ? <Loader2 className="w-3 h-3 animate-spin" /> : <Percent className="w-3 h-3" />}
                  Applica sconto
                </button>
              </div>
            </div>
          )}
          {rows.length === 0 && (
            <div className="text-sm text-xd-muted text-center py-6 border border-dashed border-xd-border rounded-lg">
              Questo prodotto non ha ancora varianti. Aggiungine una qui sopra per iniziare a venderlo.
            </div>
          )}
          {rows.map((v) => (
            <div
              key={v.id}
              data-testid={`variant-row-${v.id}`}
              className={`bg-white rounded-lg border p-4 space-y-3 transition-colors ${v.dirty ? "border-xd-orange" : "border-xd-border"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <label className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-cyanHover">Titolo variante</label>
                  <Input
                    value={v.title || ""}
                    onChange={(e) => patch(v.id, { title: e.target.value })}
                    data-testid={`variant-title-${v.id}`}
                    className="mt-1"
                    placeholder="Es. 8+128GB — Schermo 10.1"
                  />
                </div>
                <label className="flex items-center gap-2 flex-shrink-0 cursor-pointer mt-6">
                  <Switch
                    checked={!!v.available}
                    onCheckedChange={(val) => patch(v.id, { available: val })}
                    data-testid={`variant-toggle-${v.id}`}
                  />
                  <span className={`text-xs font-medium ${v.available ? "text-emerald-600" : "text-red-500"}`}>
                    {v.available ? "Disponibile" : "Esaurito"}
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted">Prezzo</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={v.price ?? 0}
                    onChange={(e) => patch(v.id, { price: e.target.value })}
                    data-testid={`variant-price-${v.id}`}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted">Prezzo pieno</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={v.compare_at_price ?? ""}
                    onChange={(e) => patch(v.id, { compare_at_price: e.target.value })}
                    data-testid={`variant-cap-${v.id}`}
                    className="mt-1"
                    placeholder="—"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted">SKU</label>
                  <Input
                    value={v.sku || ""}
                    onChange={(e) => patch(v.id, { sku: e.target.value })}
                    data-testid={`variant-sku-${v.id}`}
                    className="mt-1"
                    placeholder="—"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => removeVariant(v)}
                    disabled={rows.length <= 1 || deleting[v.id]}
                    data-testid={`variant-delete-${v.id}`}
                    title={rows.length <= 1 ? "Non puoi eliminare l'ultima variante" : "Elimina variante"}
                    className="text-xs text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {deleting[v.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    Elimina
                  </button>
                  <button
                    type="button"
                    onClick={() => copyVariantLink(v)}
                    data-testid={`variant-copy-link-${v.id}`}
                    title="Copia link diretto a questa variante (già selezionata all'apertura)"
                    className="text-xs text-xd-cyanHover hover:text-xd-cyan inline-flex items-center gap-1"
                  >
                    <Link2 className="w-3 h-3" /> Copia link
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {v.dirty && (
                    <button
                      type="button"
                      onClick={() => reset(v.id)}
                      data-testid={`variant-reset-${v.id}`}
                      className="text-xs text-xd-muted hover:text-xd-text inline-flex items-center gap-1"
                    >
                      <RotateCcw className="w-3 h-3" /> Annulla
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={!v.dirty || saving[v.id]}
                    onClick={() => save(v)}
                    data-testid={`variant-save-${v.id}`}
                    className="btn-cta rounded-lg px-4 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  >
                    {saving[v.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Salva
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
